import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../../utils/invoiceHelpers';
import voidService from '../../services/api/voidService';

/**
 * VoidModal Component
 * Handles transaction void confirmation and processing
 */
const VoidModal = ({ 
  isOpen, 
  onClose, 
  transaction, 
  onVoidSuccess 
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [eligibility, setEligibility] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedReason('');
      setCustomReason('');
      setError('');
      setIsProcessing(false);
      
      // Check void eligibility when modal opens
      const checkEligibility = async () => {
        if (!transaction?.id) return;
        
        try {
          const eligibilityData = await voidService.checkVoidEligibility(transaction.id);
          setEligibility(eligibilityData);
          
          if (!eligibilityData.eligible) {
            setError(eligibilityData.reason);
          }
        } catch (err) {
          setError('Unable to check void eligibility');
        }
      };
      
      if (transaction?.id) {
        checkEligibility();
      }
    }
  }, [isOpen, transaction?.id]);

  const handleVoid = async () => {
    if (!selectedReason) {
      setError('Please select a reason for the void');
      return;
    }

    if (selectedReason === 'other' && !customReason.trim()) {
      setError('Please specify the reason for void');
      return;
    }

    const finalReason = selectedReason === 'other' 
      ? customReason.trim() 
      : voidService.getVoidReasons().find(r => r.value === selectedReason)?.label;

    try {
      setIsProcessing(true);
      setError('');
      
      const result = await voidService.processVoid(transaction.id, finalReason);
      
      if (result.success) {
        onVoidSuccess && onVoidSuccess(result);
        onClose();
      } else {
        setError(result.message || 'Failed to void transaction');
      }
    } catch (err) {
      console.error('Void error:', err);
      
      // Handle specific error cases
      if (err.response?.data?.canRefund) {
        setError(err.response.data.error + ' You can issue a refund instead.');
      } else {
        setError(err.response?.data?.error || 'Failed to void transaction');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTimeRemaining = (timeRemaining) => {
    if (!timeRemaining) return '';
    
    const { hours, minutes } = timeRemaining;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getWarningMessage = () => {
    if (!eligibility) return '';
    
    if (!eligibility.eligible) {
      return eligibility.reason;
    }
    
    if (eligibility.warningLevel === 'urgent') {
      return `⚠️ Void window closes soon (${formatTimeRemaining(eligibility.timeRemaining)} remaining)`;
    }
    
    if (eligibility.warningLevel === 'warning') {
      return `Void window closes in ${formatTimeRemaining(eligibility.timeRemaining)}`;
    }
    
    return `Void available until settlement (${formatTimeRemaining(eligibility.timeRemaining)} remaining)`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Void Transaction</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isProcessing}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Transaction Details */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">Transaction: <span className="font-medium">#{transaction?.transactionReference || transaction?.id}</span></p>
          <p className="text-sm text-gray-600">Amount: <span className="font-medium">{formatCurrency(transaction?.amount || 0)}</span></p>
          {transaction?.cardType && (
            <p className="text-sm text-gray-600">Card: <span className="font-medium">{transaction.cardType} ending in {transaction.cardLastFour || '****'}</span></p>
          )}
        </div>

        {/* Warning Message */}
        {eligibility && (
          <div className={`mb-4 p-3 rounded-lg ${
            !eligibility.eligible 
              ? 'bg-red-50 text-red-700 border border-red-200'
              : eligibility.warningLevel === 'urgent'
              ? 'bg-orange-50 text-orange-700 border border-orange-200'
              : 'bg-blue-50 text-blue-700 border border-blue-200'
          }`}>
            <p className="text-sm">{getWarningMessage()}</p>
          </div>
        )}

        {/* Void Reason Selection */}
        {eligibility?.eligible && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for void: *
              </label>
              <select
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                disabled={isProcessing}
              >
                <option value="">Select reason...</option>
                {voidService.getVoidReasons().map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Reason Input */}
            {selectedReason === 'other' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Please specify:
                </label>
                <textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                  rows="3"
                  placeholder="Enter the reason for void..."
                  disabled={isProcessing}
                />
              </div>
            )}

            <div className="mb-4 p-3 bg-orange-50 rounded-lg">
              <p className="text-sm text-orange-700">
                ⚠️ This action cannot be undone. The transaction will be canceled before settlement.
              </p>
            </div>
          </>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            disabled={isProcessing}
          >
            Cancel
          </button>
          {eligibility?.eligible && (
            <button
              onClick={handleVoid}
              disabled={isProcessing || !selectedReason || (selectedReason === 'other' && !customReason.trim())}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isProcessing && (
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isProcessing ? 'Voiding...' : 'Void Transaction'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoidModal;